/**
 * TopUsersTable - Displays top users by session count.
 * Shows user initials, session count, primary mode, and token usage.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Users } from 'lucide-react'
import type { TopUsersDataPoint } from '@/types/adoption'

export interface TopUsersTableProps {
  /** Array of top user data */
  data: TopUsersDataPoint[]
  /** Loading state */
  isLoading?: boolean
}

/**
 * Get initials from email or user identifier
 */
function getInitials(user: string): string {
  // Extract first letter of email prefix
  const prefix = user.split('@')[0]
  return prefix.charAt(0).toUpperCase()
}

/**
 * Format number with locale-aware thousand separators
 */
function formatNumber(value: number): string {
  return value.toLocaleString()
}

/**
 * Mode badge colors
 */
const modeColors: Record<string, string> = {
  Interactive: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Background: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Chatbot: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  WorkItemAnalysis: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
}

/**
 * Loading skeleton for table rows
 */
function LoadingSkeleton() {
  return (
    <div data-testid="top-users-loading" className="space-y-2 p-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}

/**
 * Table component displaying top platform users.
 * 
 * @example
 * ```tsx
 * <TopUsersTable 
 *   data={[
 *     { user: 'alice@example.com', sessionCount: 150, totalTokens: 50000, primaryMode: 'Interactive' }
 *   ]} 
 * />
 * ```
 */
export function TopUsersTable({ data, isLoading }: TopUsersTableProps) {
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Users className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No users found in this period</p>
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">#</TableHead>
          <TableHead>User</TableHead>
          <TableHead className="text-right">Sessions</TableHead>
          <TableHead>Mode</TableHead>
          <TableHead className="text-right">Tokens</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((user, index) => (
          <TableRow key={user.user}>
            <TableCell className="font-medium text-muted-foreground">
              {index + 1}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  {getInitials(user.user)}
                </div>
                <span className="font-medium">{user.user}</span>
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">
              {user.sessionCount}
            </TableCell>
            <TableCell>
              <span 
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  modeColors[user.primaryMode] ?? 'bg-gray-100 text-gray-800'
                }`}
              >
                {user.primaryMode}
              </span>
            </TableCell>
            <TableCell className="text-right font-mono">
              {formatNumber(user.totalTokens)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
